import Service from '@ember/service';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import { findRecord } from '@warp-drive/utilities/json-api';
import type SessionContextService from './session-context';
import type { Store } from '@warp-drive/core';
import type FeatureService from 'tio-common/services/features';
import type PreRegisterService from './pre-register';
import type { CompanySettingExtension as CompanySettingModel } from 'tio-data/data/resources/company-setting.ext';
import type {
  CompanySettingModules,
  ResolvedPhoneCaptureConfig,
} from 'tio-data/data/resources/company-setting.type';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type { PlanExtension as PlanModel } from 'tio-data/data/resources/plan.ext';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type SessionService from './session';
import { normalizeCompanyTheme } from 'tio-common/utils/company-theme';
import {
  getCurrentDemoCompany,
  getCurrentDemoCompanySettings,
  getCurrentDemoPlans,
  type DemoPlanSummary,
} from 'tio-employee/utils/career-pathways-demo-data';

type PlanFeatureSummary = Pick<PlanModel, 'cadence' | 'isWellness'> | DemoPlanSummary;

export default class PartnerService extends Service {
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;
  @service declare features: FeatureService;
  @service declare preRegister: PreRegisterService;
  @service declare demoMode: DemoModeService;
  @service declare session: SessionService;
  #lastThemeRead = { key: '', at: 0 };

  /** Revalidates the active company's public theme; foreground events are throttled to one minute. */
  refreshTheme = async (force = false) => {
    if (this.isDemoMode) return;
    const company = this.sessionContext.currentCompany;
    const key = this.session.isAuthenticated ? `company:${company?.id ?? ''}` : 'public';
    const now = Date.now();
    if (!force && key === this.#lastThemeRead.key && now - this.#lastThemeRead.at < 60_000) return;
    this.#lastThemeRead = { key, at: now };

    if (!this.session.isAuthenticated) {
      await this.preRegister.load();
      return;
    }
    if (!company?.id) return;
    try {
      await this.store.request(findRecord<CompanyModel>('company', company.id, { reload: true }));
    } catch (error) {
      if (this.sessionContext.currentCompany?.id === company.id) company.publicTheme = null;
      console.warn('Unable to refresh company theme', error);
    }
  };

  /** The authenticated company owns its theme, including an explicit native fallback. */
  get publicTheme() {
    if (this.isDemoMode) return undefined;
    const company = this.session.isAuthenticated
      ? this.sessionContext.currentCompany
      : this.preRegister.sparseCompany;
    if (!company?.id) return undefined;
    return normalizeCompanyTheme(company.publicTheme, company.id);
  }

  // One demo predicate shared with the identity swap: branding + identity agree
  // (and the prod staff-gate applies consistently), instead of a raw URL check.
  get isDemoMode() {
    return this.demoMode.isActive;
  }

  get company() {
    if (this.isDemoMode) {
      return getCurrentDemoCompany();
    }

    return this.sessionContext.currentCompany ?? this.preRegister.sparseCompany;
  }

  get companySettings() {
    if (this.isDemoMode) {
      return getCurrentDemoCompanySettings();
    }

    return (this.sessionContext.currentCompany?.companySetting ??
      this.preRegister.settings) as Partial<CompanySettingModel>;
  }

  /**
   * The phone-capture config the API already resolved: company switch AND server
   * environment gate applied, every key present.
   *
   * Not read from `settingModules.phoneCapture` — that is the stored company half
   * only, so it reports a surface as on in an environment the API will refuse to
   * serve. Undefined before any company is resolved, which callers treat as off.
   */
  get phoneCapture(): ResolvedPhoneCaptureConfig | undefined {
    return this.companySettings?.phoneCapture;
  }

  get settingModules() {
    return this.companySettings?.modules as CompanySettingModules | undefined;
  }

  get supportModule() {
    return this.settingModules?.support;
  }

  get isPhoneSupportEnabled() {
    return (
      !this.supportModule ||
      this.supportModule?.tioPhone?.isEnabled ||
      this.supportModule?.partnerPhone
    );
  }

  get isEmailSupportEnabled() {
    return (
      !this.supportModule ||
      this.supportModule?.tioEmail?.isEnabled ||
      this.supportModule?.partnerEmail
    );
  }

  // this can also be checked from the Zendesk service
  get isZendeskEnabled() {
    return !this.supportModule || this.supportModule?.zendesk?.isEnabled;
  }

  // Absence of the flag means Forethought is enabled (no migration backfills
  // existing company_settings jsonb), so only an explicit false disables it.
  get isForethoughtChatEnabled() {
    return this.supportModule?.forethoughtChat?.isEnabled !== false;
  }

  get isFaqEnabled() {
    return !this.supportModule || this.supportModule?.faqs?.isEnabled;
  }

  get isAnySupportEnabled() {
    if (
      !this.supportModule ||
      this.isPhoneSupportEnabled ||
      this.isEmailSupportEnabled ||
      this.isZendeskEnabled
    ) {
      return true;
    }
    return false;
  }

  get subDomain() {
    if (this.isDemoMode) {
      return getCurrentDemoCompanySettings().subDomain ?? '';
    }

    return (
      this.sessionContext.currentCompany?.companySetting?.subDomain ?? this.preRegister.subdomain
    );
  }
  get returnToPartnerUrlObj() {
    return this.companySettings.returnToPartnerUrl;
  }

  get partnerUrlString() {
    const url = this.companySettings.returnToPartnerUrl;
    const instride = url?.base === 'instride.com';
    const path = instride ? 'account/dashboard/next-steps' : url?.path;
    return url ? `https://${url.subdomain}.${url.base}${path ? `/${path}` : ''}` : null;
  }

  get isLendkey() {
    if (!this.subDomain) {
      return false;
    }
    // All partners except:
    //   Boeing, Harbor Capital, Colorado Dept of Higher Ed, Northshore
    return !['boeing', 'harborcapital', 'cdhe', 'nseeh'].includes(this.subDomain);
  }

  get shouldShowRefinancingAwknowledgement() {
    /* This would be an opt in on the repayment options
      refinancing page. The current alert supports two lines of text.
      What would we support considering this will be an admin level
      setting?
    */
    return false;
  }

  get shouldShowCustomRefinancingWarning() {
    return this.isFedReserve;
  }

  // Used in repayment options refinancing
  get shouldShowRefinanceApply() {
    return !['boeing', 'nintendo-vdk'].includes(this.subDomain);
  }

  /**
   * Returns URL depending on the company ID.
   */
  get credibleHref() {
    return this.isMeredith
      ? 'https://www.credible.com/refinance-student-loans?utm_source=meredith&utm_medium=referral&utm_campaign=student_loan_refi'
      : 'https://credible.com';
  }

  /**
   * Returns URL depending on the company ID.
   */
  get lendkeyHref() {
    if (this.isFordMotors) {
      return 'https://www.lendkey.com/partners/ford/?sk=AFF-Tuition.io-Ford&utm_source=AFF-Tuition.io&utm_medium=referral&utm_campaign=AFF';
    }

    if (this.isMeredith) {
      return 'https://www.lendkey.com/partners/meredith/?sk=AFF-Tuition.io-Meredith&utm_source=AFF-Tuition.io&utm_medium=referral&utm_campaign=AFF';
    }

    return 'https://lendkey.com';
  }

  get isPslfEligible() {
    return this.companySettings.partnerType === 'PSLF_ELIGIBLE';
  }

  get isPrivate() {
    return this.companySettings.partnerType === 'PRIVATE';
  }

  get isIdrEnabled() {
    if (this.sessionContext.isFamilyMember || this.isPslfEnabled) {
      return false;
    }

    return this.isPrivate;
  }

  get isPslfEnabled() {
    if (this.sessionContext.isFamilyMember) {
      return false;
    }
    return this.companySettings?.modules?.pslf?.enabled;
  }

  get isSyfEnabled() {
    return this.companySettings?.modules?.syf?.isEnabled;
  }

  get isEnabledPriorEmployerPslf() {
    return this.isPslfEnabled && this.companySettings?.modules?.pslf?.isEnabledPriorEmployer;
  }

  get isTaOnly() {
    return this.companySettings?.tuitionAssistance?.isTaOnly;
  }

  get doesAllowFamilyPlan() {
    return this.companySettings?.allowFamilyPlan;
  }

  get hasNewTuitionAssistance() {
    if (this.isDemoMode) {
      return !!this.company?.taEnabled;
    }

    // Backend taEnabled already checks for launched program templates
    return !!this.company?.taEnabled;
  }

  loadPlansForCurrentCompany = async () => {
    if (this.isDemoMode) {
      return getCurrentDemoPlans();
    }

    if (!this.company?.id) {
      return;
    }

    const { content } = (await this.store.request(
      findRecord<CompanyModel>('company', this.company?.id as string, {
        include: 'plans',
        reload: true,
      })
    )) as { content: { data: CompanyModel } };
    const plans = content.data.plans;
    return plans;
  };

  doPlansHaveContributions(plans: PlanFeatureSummary[] = []) {
    const plansWithContributions = plans.filter((plan) => plan.cadence !== 'NEVER');
    return plansWithContributions.length > 0;
  }

  doPlansHaveWellness(plans: PlanFeatureSummary[] = []) {
    const plansWithWellness = plans.filter((plan) => plan.isWellness);
    return !!plansWithWellness.length;
  }

  plansState = trackedFunction(this, async () => {
    const plans = await this.loadPlansForCurrentCompany();
    return plans;
  });

  get plans() {
    return this.plansState.value || [];
  }

  get hasContributions() {
    return this.doPlansHaveContributions(this.plans);
  }

  get hasWellness() {
    return this.doPlansHaveWellness(this.plans);
  }

  // ── Specific Partners ──
  get isBoeing() {
    return this.subDomain === 'boeing';
  }

  get isStarbucks() {
    return this.subDomain === 'starbucks';
  }

  get isTrilogy() {
    return this.subDomain === 'trilogy';
  }

  get isFedReserve() {
    return this.subDomain === 'frb';
  }

  get isHoraceMann() {
    return this.subDomain === 'horacemann';
  }

  get isMeredith() {
    return this.subDomain === 'meredith';
  }

  get isNorthshore() {
    return this.subDomain === 'nseeh';
  }

  get isFordMotors() {
    return this.subDomain === 'ford';
  }
}
